import { MouseEvent, useRef, useState } from 'react';

import { COMPANY_DETAIL_MENU } from '~constant/company';
import { HEADER_HEIGHT } from '~constant/layout';
import * as S from '~features/company/Header/style';
import useDraggable from '~features/company/hooks/useDraggable';
import { useSuspensedCompanyInfo } from '~hooks/query/company/useCompanyInfo';
import { useSuspensedCompanyStates } from '~hooks/query/company/useCompanyStates';
import useIntersectionObserver from '~hooks/useIntersectionObserver';
import useMedia from '~hooks/useMedia';
import { ValueOf } from '~types/common';

import ActionButton from '../CompanyInfo/ActionButton';
import DetailNav from '../DetailNav';

function CompanyMenu({ selectedMenu }: { selectedMenu: ValueOf<typeof COMPANY_DETAIL_MENU> }) {
  const [menu, setMenu] = useState<ValueOf<typeof COMPANY_DETAIL_MENU>>(selectedMenu);
  const { data: companyInfo } = useSuspensedCompanyInfo();
  const { data: companyStates } = useSuspensedCompanyStates();
  const { isDesktop } = useMedia();

  // sticky
  const ref = useRef<HTMLDivElement | null>(null);

  const entry = useIntersectionObserver(ref, {
    rootMargin: `-${HEADER_HEIGHT}px`,
    threshold: 0,
  });

  // menuslider
  const { sliderRef, inDragging, startDragging, stopDragging } = useDraggable<HTMLDivElement>();

  const handleScrollBehavior = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.target || !document) return;

    const scrollTargetId =
      (e.target as HTMLDivElement).getAttribute('href') ||
      (e.target as HTMLDivElement).parentElement?.getAttribute('href');

    if (!scrollTargetId || !scrollTargetId.split('#')[1]) return;

    const scrollTarget = document.querySelector(scrollTargetId || '');

    setMenu(scrollTargetId.split('#')[1] as ValueOf<typeof COMPANY_DETAIL_MENU>);

    if (!scrollTarget) return;
    scrollTarget.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <div ref={ref} />
      <S.StickyEmptyBlock isSticky={entry?.isIntersecting || false} />

      <S.MenuWrapper isSticky={entry?.isIntersecting || false}>
        <div
          onClick={handleScrollBehavior}
          ref={sliderRef}
          onTouchStart={startDragging}
          onTouchMove={inDragging}
          onTouchEnd={stopDragging}
          onMouseMove={inDragging}
          onMouseDown={startDragging}
          onMouseUp={stopDragging}
          onMouseOver={stopDragging}>
          <DetailNav isClosedCompany={companyStates?.closed} selectedMenu={menu} />

          <S.ActionButtonGroup isVisibility={!!entry?.isIntersecting}>
            {isDesktop && <ActionButton companyInfo={companyInfo} />}
          </S.ActionButtonGroup>
        </div>
      </S.MenuWrapper>
    </>
  );
}

export default CompanyMenu;
